/**
 * Legacy plan id -> grandfathered plan-doc id (free/growth/business/…).
 * Grandfathered tenants keep their live Stripe subscription untouched; this
 * map only governs how the app resolves plan docs for an existing planId.
 * Keep in sync with riverr-scripts/plans/planDefinitions.js.
 *
 * V2/V3/V4 public plans are real docs — do NOT alias them onto the
 * grandfathered ids or fee/limit lookups break.
 */
export const PLAN_ALIASES = {
  RIVERRFREE: "free",
  NOV24STARTER: "free",
  SELLERCORE: "free",
  RIVERRPRO49: "growth",
  RIVERRPRO99: "growth",
  NOV24PRO: "growth",
  NOV24BUSINESS: "growth",
  SELLERPLUS: "growth",
  WORKFLOWPRO: "growth",
  NOV24BUSINESS299: "business",
  FULFILLMENTHUB: "business",
  ENTERPRISE399: "enterprise",
  ENTERPRISE499: "enterprise",
};

/** Gating tier for free/growth/business — collapses V2/V3/V4 onto the same tier. */
export const PLAN_TIERS = {
  free: "free",
  freeV2: "free",
  freeV3: "free",
  freeV4: "free",
  growth: "growth",
  growthV2: "growth",
  growthV3: "growth",
  growthV4: "growth",
  business: "business",
  businessV2: "business",
  businessV3: "business",
  businessV4: "business",
  enterprise: "enterprise",
  franchise: "franchise",
  franchiseLocation: "franchiseLocation",
};

/** Resolve any (possibly legacy) plan id to its stored plan-doc id. */
export const resolvePlanId = (planId) =>
  planId ? PLAN_ALIASES[planId] || planId : null;

/** Resolve a plan id to a gating tier (free/growth/business/enterprise/…). */
export const resolvePlanTier = (planId) => {
  const resolved = resolvePlanId(planId);
  if (!resolved) return null;
  return PLAN_TIERS[resolved] || null;
};

/** True when this public pricing card is the tenant's current tier. */
export const isCurrentPricingTier = (planId, tier) => {
  if (!tier || tier.isContact) return false;
  const activeTier = resolvePlanTier(planId) || "free";
  return resolvePlanTier(tier.planId) === activeTier;
};

/** Current public generation — used for V4-only feature tightening. */
export const isV4PlanId = (planId) =>
  planId === "freeV4" || planId === "growthV4" || planId === "businessV4";

/**
 * Display labels for grandfathered plan docs (still live for existing
 * tenants). The public pricing cards use V2 ids; Billing still needs these.
 */
export const GRANDFATHERED_PLAN_DISPLAY = {
  free: { name: "Free", priceCents: 0 },
  growth: { name: "Growth", priceCents: 19900 },
  business: { name: "Business", priceCents: 34900 },
};

export const PRICINGS = {
  RIVERRFREE: {
    createdAt: {
      _seconds: 1648486617,
      _nanoseconds: 257000000,
    },
    price: 0,
    priceCents: 0,
    perOrderChargeCents: 10,
    pricePeriod: "Forever",
    features: [
      {
        id: "Unlimited Orders",
        name: "Unlimited Orders",
        active: true,
      },
      {
        id: "Create and list unlimited products",
        name: "Create and list unlimited products",
        active: true,
      },
      {
        id: "Print-ready files for every item",
        name: "Print-ready files for every item",
        active: true,
      },
      {
        id: "Instantly generate purchase order",
        name: "Instantly generate purchase order",
        active: true,
      },
      {
        id: "Manage blank inventory",
        name: "Manage blank inventory",
        active: true,
      },
    ],
    perOrderChargeStr: "10 cents per order",
    priceStr: "Free",
    perOrderCharge: 0.1,
    id: "RIVERRFREE",
    updatedAt: {
      _seconds: 1648486617,
      _nanoseconds: 257000000,
    },
    description: "Free Forever",
    name: "Basic",
  },
  RIVERRPRO49: {
    description: "Riverr Pro",
    perOrderChargeCents: 5,
    perOrderCharge: 0.05,
    perOrderChargeStr: "5 cents per order",
    features: [
      {
        id: "Instantly generate purchase order",
        name: "Instantly generate purchase order",
        active: true,
      },
      {
        name: "Live ordering",
        id: "Live ordering",
        active: true,
      },
      {
        id: "Manage blank inventory",
        name: "Manage blank inventory",
        active: true,
      },
      {
        id: "Monitor price and supply levels from suppliers",
        name: "Monitor price and supply levels from suppliers",
        active: true,
      },
      {
        active: true,
        name: "Unlimited Orders",
        id: "Unlimited Orders",
      },
      {
        name: "Unlimited Channels",
        id: "Unlimited Channels",
        active: true,
      },
    ],
    price: 59,
    priceCents: 5900,
    pricePeriod: "month",
    updatedAt: {
      _seconds: 1648486618,
      _nanoseconds: 526000000,
    },
    createdAt: {
      _seconds: 1648486618,
      _nanoseconds: 526000000,
    },
    id: "RIVERRPRO49",
    priceStr: "59/Month",
    name: "Scale",
  },
  RIVERRPRO99: {
    id: "RIVERRPRO99",
    updatedAt: {
      _seconds: 1648486619,
      _nanoseconds: 533000000,
    },
    features: [
      {
        id: "Barcode workflow",
        name: "Barcode workflow",
        active: true,
      },
      {
        id: "Sort/filter items based on workflow",
        name: "Sort/filter items based on workflow",
        active: true,
      },
      {
        id: "Automated bin system",
        name: "Automated bin system",
        active: true,
      },
      {
        id: "Scan to print for DTG",
        name: "Scan to print for DTG",
        active: true,
      },
      {
        id: "Automated Image Processing",
        name: "Automated Image Processing",
        active: true,
      },
    ],
    name: "Production",
    perOrderChargeStr: "5 cents per order",
    priceCents: 9900,
    createdAt: {
      _seconds: 1648486619,
      _nanoseconds: 533000000,
    },
    priceStr: "99/Month",
    description: "Riverr Plus",
    pricePeriod: "month",
    perOrderCharge: 0.05,
    price: 99,
    perOrderChargeCents: 5,
  },
};

export const RIVERR_APP_PRICINGS = {
  SELLERCORE: {
    perOrderChargeCents: 0,
    featureHeading: "",
    features: [
      {
        id: "Unlimited Orders",
        name: "Unlimited Orders",
        active: true,
      },
      {
        name: "Connect all of your stores from every platform",
        id: "Connect all of your stores from every platform",
        active: true,
      },
      {
        id: "Create and list unlimited products",
        name: "Create and list unlimited products",
        active: true,
      },
      {
        id: "Choose from thousands of products for fulfillment",
        name: "Choose from thousands of products for fulfillment",
        active: true,
      },
      {
        active: true,
        name: "Integrate directly with fulfillment Hub users",
        id: "Integrate directly with Riverr fulfillment Hub users",
      },
    ],
    priceStr: "Free",
    perOrderCharge: 0,
    price: 0,
    name: "Seller Core",
    description: "Seller Core",
    priceCents: 0,
    id: "SELLERCORE",
    pricePeriod: "month",
    perOrderChargeStr: "0 cents per order",
    createdAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
    updatedAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
  },
  SELLERPLUS: {
    perOrderChargeCents: 5,
    featureHeading: "Everything from Seller Core, as well as:",
    features: [
      {
        id: "Create and use your own blank products",
        name: "Create and use your own blank products",
        active: true,
      },
      {
        name: "Create customizable products for end users to purchase",
        id: "Create customizable products for end users to purchase",
        active: true,
      },
      {
        id: "Connect a custom domain for your customizable products",
        name: "Connect a custom domain for your customizable products",
        active: true,
      },
      {
        id: "Pull all of your customized orders into your dashboard",
        name: "Pull all of your customized orders into your dashboard",
        active: true,
      },
      {
        active: true,
        name: "Integrated Checkout Process",
        id: "Integrated Checkout Process",
      },
    ],
    priceStr: "29/month",
    perOrderCharge: 0.5,
    price: 29,
    name: "Seller Plus",
    description: "Seller Plus",
    priceCents: 2900,
    id: "SELLERPLUS",
    pricePeriod: "month",
    perOrderChargeStr: "5 cents per order",
    createdAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
    updatedAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
  },
  WORKFLOWPRO: {
    perOrderChargeCents: 5,
    featureHeading: "Everything from Seller Plus, as well as:",
    features: [
      {
        id: "Automated inventory management",
        name: "Automated inventory management",
        active: true,
      },
      {
        name: "Automated purchase order creation",
        id: "Automated purchase order creation",
        active: true,
      },
      {
        id: "Complete barcode enabled manufacturing flow",
        name: "Complete barcode enabled manufacturing flow",
        active: true,
      },
      {
        id: "Direct integrations with DTG printers",
        name: "Direct integrations with DTG printers",
        active: true,
      },
      {
        active: true,
        name: "Direct integrations with shipping",
        id: "Direct integrations with shipping",
      },
    ],
    priceStr: "99/month",
    perOrderCharge: 0.5,
    price: 99,
    name: "Workflow Pro",
    description: "Workflow Pro",
    priceCents: 9900,
    id: "WORKFLOWPRO",
    pricePeriod: "month",
    perOrderChargeStr: "5 cents per order",
    createdAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
    updatedAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
  },
  FULFILLMENTHUB: {
    perOrderChargeCents: 5,
    featureHeading: "Everything from Workflow Pro, as well as:",
    features: [
      {
        id: "Link to send users so that they can import your catalog directly",
        name: "Link to send users so that they can import your catalog directly",
        active: true,
      },
      {
        id: "Added to list of fulfillment partners for all users to use",
        name: "Added to list of fulfillment partners for all users to use",
        active: true,
      },
      {
        active: true,
        name: "Option to be a part of our fulfillment network",
        id: "Option to be a part of our fulfillment network",
      },
    ],
    priceStr: "199/month",
    perOrderCharge: 0.5,
    price: 199,
    name: "Fulfillment Hub",
    description: "$3999 Setup Fee",
    priceCents: 19900,
    id: "FULFILLMENTHUB",
    pricePeriod: "month",
    perOrderChargeStr: "5 cents per order",
    createdAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
    updatedAt: {
      _seconds: 1697261470,
      _nanoseconds: 954000000,
    },
  },
};

export const ENTERPRISE_PRICING = {
  id: "ENTERPRISE399",
  updatedAt: {
    _seconds: 1683612176,
    _nanoseconds: 44000000,
  },
  features: [],
  name: "Enterprise $399",
  createdAt: {
    _seconds: 1683612176,
    _nanoseconds: 44000000,
  },
  priceStr: "399$/Month",
  price: 399,
  roles: ["enterprise_plan"],
  description: "Enterprise Plan $399",
  priceCents: 39900,
  pricePeriod: "month",
};

export const NOV24STARTER_PRICING = {
  features: [
    {
      id: "Company Store Customizer",
      name: "Company Store Customizer",
      active: true,
    },
    {
      id: "1 Company Store",
      name: "1 Company Store",
      active: true,
    },
    {
      id: "Unlimited Seller Storefronts",
      name: "Unlimited Seller Storefronts",
      active: true,
    },
    {
      id: "3% per order Storefront Transaction Fee",
      name: "3% per order Storefront Transaction Fee",
      active: true,
    },
  ],
  description: "Starter",
  id: "NOV24STARTER",
  name: "Starter",
  perOrderCharge: 0,
  perOrderChargeCents: 0,
  perOrderChargeStr: "0 cents per order",
  price: 0,
  priceCents: 0,
  pricePeriod: "month",
  priceStr: "Free",
  createdAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
  updatedAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
};

export const NOV24PRO_PRICING = {
  features: [
    {
      id: "Company Store Customizer",
      name: "Company Store Customizer",
      active: true,
    },
    {
      id: "Unlimited Company Store",
      name: "Unlimited Company Store",
      active: true,
    },
    {
      id: "Unlimited Seller Storefronts",
      name: "Unlimited Seller Storefronts",
      active: true,
    },
    {
      id: "3% per order Storefront Transaction Fee",
      name: "3% per order Storefront Transaction Fee",
      active: true,
    },
    {
      id: "Manufacturing Support",
      name: "Manufacturing Support",
      active: true,
    },
    {
      id: "Multi-Channel Integration",
      name: "Multi-Channel Integration",
      active: true,
    },
    {
      id: "5 cents Multi-Channel Order Transaction Fee",
      name: "5 cents Multi-Channel Order Transaction Fee",
      active: true,
    },
  ],
  description: "Pro",
  id: "NOV24PRO",
  name: "Pro",
  perOrderCharge: 0.05,
  perOrderChargeCents: 5,
  perOrderChargeStr: "5 cents per order",
  price: 99,
  priceCents: 9900,
  pricePeriod: "month",
  priceStr: "$99/month",
  createdAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
  updatedAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
};

export const NOV24BUSINESS299_PRICING = {
  id: "NOV24BUSINESS299",
  name: "Business",
  description: "Business",
  price: 299,
  priceCents: 29900,
  pricePeriod: "month",
  priceStr: "$299/month",
  createdAt: {
    _seconds: 1749647084,
    _nanoseconds: 1000000,
  },
  updatedAt: {
    _seconds: 1749647084,
    _nanoseconds: 1000000,
  },
  perOrderCharge: "0.05",
  perOrderChargeCents: "5",
  perOrderChargeStr: "5 cents per order",
  features: [
    {
      id: "Company Store Customizer",
      name: "Company Store Customizer",
      active: true,
    },
    {
      id: "Unlimited Company Store",
      name: "Unlimited Company Store",
      active: true,
    },
    {
      id: "Unlimited Seller Storefronts",
      name: "Unlimited Seller Storefronts",
      active: true,
    },
    {
      id: "No Storefront Transaction Fee",
      name: "No Storefront Transaction Fee",
      active: true,
    },
    {
      id: "Manufacturing Support",
      name: "Manufacturing Support",
      active: true,
    },
    {
      id: "Multi-Channel Integration",
      name: "Multi-Channel Integration",
      active: true,
    },
    {
      id: "Seller Multi-Channel Integration",
      name: "Seller Multi-Channel Integration",
      active: true,
    },
    {
      id: "5 cents Multi-Channel Order Transaction Fee",
      name: "5 cents Multi-Channel Order Transaction Fee",
      active: true,
    },
  ],
};

/**
 * Public pricing cards (Free / Growth / Business / Enterprise).
 * Display copy matches marketing. `planId` is the Firestore plan the upgrade
 * flow activates (V4). Grandfathered tenants keep existing plan docs until
 * they choose a new plan here.
 */
export const PRICING_TIERS = [
  {
    planId: "freeV4",
    name: "Free",
    isFree: true,
    priceLabel: "$0",
    priceUnit: "/mo",
    description:
      "You're a one-person shop, or you're testing before you commit.",
    cta: "Start free",
    ctaVariant: "outline",
    limits: [
      { label: "Orders", value: "20", suffix: "/mo" },
      { label: "Client portals", value: "5" },
      { label: "Users", value: "1" },
      { label: "Locations", value: "1" },
      { label: "Third-party orders", value: "Not included", muted: true },
    ],
    noCommission: "0% commission on merch & company stores. Ever.",
    features: [],
  },
  {
    planId: "growthV4",
    name: "Growth",
    popular: true,
    price: 299,
    priceUnit: "/mo",
    description:
      "You have staff, client accounts, and stores running year-round.",
    cta: "Get started",
    ctaVariant: "primary",
    limits: [
      { label: "Orders", value: "200", suffix: "/mo" },
      { label: "Client portals", value: "25" },
      { label: "Users", value: "5", suffix: " · $15 ea" },
      { label: "Locations", value: "1" },
      {
        label: "Third-party orders",
        value: "$0.30",
        suffix: "/item",
        accent: true,
      },
    ],
    noCommission: "0% commission on merch & company stores. Ever.",
    features: [],
  },
  {
    planId: "businessV4",
    name: "Business",
    price: 449,
    priceUnit: "/mo",
    description:
      "You've opened a second building, you have a developer, or you're past 25 accounts.",
    cta: "Get started",
    ctaVariant: "primary",
    limits: [
      { label: "Orders", value: "4,000", suffix: "/mo" },
      { label: "Client portals", value: "Unlimited" },
      { label: "Users", value: "15", suffix: " · $12 ea" },
      { label: "Locations", value: "3" },
      {
        label: "Third-party orders",
        value: "$0.20",
        suffix: "/item",
        accent: true,
      },
    ],
    noCommission: "0% commission on merch & company stores. Ever.",
    plus: "Plus API access, nexus tracking, advanced inventory, priority support",
    features: [],
  },
  {
    planId: "enterprise",
    name: "Enterprise",
    isContact: true,
    priceLabel: "Custom",
    description:
      "You run locations under one parent, you want the platform under your own brand, or IT and legal are involved.",
    cta: "Talk to us",
    ctaVariant: "outline",
    limits: [
      { label: "Orders", value: "Custom" },
      { label: "Client portals", value: "Unlimited" },
      { label: "Users", value: "Unlimited" },
      { label: "Locations", value: "Unlimited" },
      { label: "Third-party orders", value: "Discounted rate", accent: true },
    ],
    noCommission: "0% commission on merch & company stores. Ever.",
    plus: "Plus franchise hierarchy, white-label platform & Shopify apps, SSO/SAML, SLA, migration services",
    features: [],
  },
];

/** Feature comparison grid rendered under the tier cards. */
export const PRICING_COMPARE_COLUMNS = [
  "Free",
  "Growth",
  "Business",
  "Enterprise",
];

export const PRICING_COMPARE_SECTIONS = [
  {
    title: "Volume & third-party orders",
    rows: [
      {
        label: "Orders per month",
        values: ["20", "200", "4,000", "Custom"],
      },
      {
        label: "Third-party order items",
        values: ["Not included", "$0.30/item", "$0.20/item", "Discounted rate"],
      },
      {
        label: "Cap per order",
        values: [false, "$15", "$10", false],
      },
      {
        label: "Commission on merch & company stores",
        values: ["None", "None", "None", "None"],
      },
    ],
  },
  {
    title: "Team & locations",
    rows: [
      {
        label: "Users included",
        values: ["1", "5", "15", "Unlimited"],
      },
      {
        label: "Extra users",
        values: ["Not available", "$15/user/mo", "$12/user/mo", "Included"],
      },
      {
        label: "Locations",
        values: ["1", "1", "3", "Unlimited"],
      },
      {
        label: "Client portals",
        values: ["5", "25", "Unlimited", "Unlimited"],
      },
    ],
  },
  {
    title: "Platform",
    rows: [
      {
        label: "Company & merch stores",
        values: [true, true, true, true],
      },
      {
        label: "Front counter POS",
        values: [true, true, true, true],
      },
      {
        label: "Production queue & ticket rail",
        values: [true, true, true, true],
      },
      {
        label: "Shopify / Etsy & third-party channels",
        values: [false, true, true, true],
      },
      { label: "API access", values: [false, false, true, true] },
      {
        label: "White-label platform",
        values: [false, false, false, true],
      },
    ],
  },
  {
    title: "Support",
    rows: [
      {
        label: "Priority support",
        values: [false, false, true, true],
      },
      {
        label: "Dedicated success manager & SLA",
        values: [false, false, false, true],
      },
    ],
  },
];

export const NOV24BUSINESS_PRICING = {
  hidden: true,
  description: "Business",
  id: "NOV24BUSINESS",
  name: "Business",
  perOrderCharge: 0.05,
  perOrderChargeCents: 5,
  perOrderChargeStr: "5 cents per order",
  price: 199,
  priceCents: 19900,
  pricePeriod: "month",
  priceStr: "$199/month",
  createdAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
  updatedAt: {
    _seconds: 1732712158,
    _nanoseconds: 378000000,
  },
  features: [
    {
      id: "Company Store Customizer",
      name: "Company Store Customizer",
      active: true,
    },
    {
      id: "Unlimited Company Store",
      name: "Unlimited Company Store",
      active: true,
    },
    {
      id: "Unlimited Seller Storefronts",
      name: "Unlimited Seller Storefronts",
      active: true,
    },
    {
      id: "No Storefront Transaction Fee",
      name: "No Storefront Transaction Fee",
      active: true,
    },
    {
      id: "Manufacturing Support",
      name: "Manufacturing Support",
      active: true,
    },
    {
      id: "Multi-Channel Integration",
      name: "Multi-Channel Integration",
      active: true,
    },
    {
      id: "Seller Multi-Channel Integration",
      name: "Seller Multi-Channel Integration",
      active: true,
    },
    {
      id: "5 cents Multi-Channel Order Transaction Fee",
      name: "5 cents Multi-Channel Order Transaction Fee",
      active: true,
    },
  ],
};
